/**
 * Things the app noticed, that the user did not ask about.
 *
 * Every other screen answers a question somebody thought to ask: Insights charts
 * the track you picked, the report summarises what you selected. This is the
 * first thing here that volunteers anything, which makes it the most valuable
 * and the easiest to ruin.
 *
 * Three rules, and they are the ticket rather than the coverage:
 *
 *   1. **It must be allowed to say nothing.** Most days there is nothing true
 *      and non-obvious to say. A list that manufactures an observation every day
 *      is wallpaper by the second week, and then the one that mattered is
 *      wallpaper too. Every rule below returns `null` far more often than not.
 *   2. **It must never be a verdict.** "You are sleeping badly" is a judgement
 *      about subject matter this app does not know. "Sleep is lower on the
 *      nights after a day tagged busy" is an observation the reader can accept
 *      or dismiss. Directions are facts; good and bad are not.
 *   3. **It must be checkable.** Every noticing carries the evidence it was
 *      drawn from — how many readings, over how many days — because an
 *      observation the reader cannot audit is one they either believe too much
 *      or ignore entirely.
 *
 * The wording lives here, like every other sentence about data, so the screen
 * and any future printed version cannot phrase the same fact differently.
 */

import type { Entry } from '../model/entry.js'
import type { Track } from '../model/track.js'
import { weekdayName, weekdayOf, type Weekday } from '../model/reminder.js'
import {
  dayKey,
  minutesSinceMidnight,
  wallClockOf,
  type Timestamp,
} from '../model/timestamp.js'
import { isQuantitative } from '../model/track.js'
import type { EpochRange } from '../ports/storage.js'
import { buildSeries, type QuantitativePoint, type SeriesPoint } from './series.js'

/**
 * What a noticing is about, for grouping and for an icon.
 *
 * Not a severity and not a score. A noticing is either worth showing or it is
 * not; ranking them by how alarming they sound is how a neutral list becomes a
 * league table of things to worry about.
 */
export type NoticingKind =
  /** A run of readings at one end of everything recorded. */
  | 'run'
  /** The same track happens at a different time on some days than others. */
  | 'timing'
  /** One day of the week stands apart from the others. */
  | 'weekday'
  /** Readings on tagged days differ from readings on untagged ones. */
  | 'tag'
  /** Recorded again after a gap. */
  | 'return'

export interface Noticing {
  /**
   * Stable for the same observation on the same data.
   *
   * Derived from the rule and what it is about, never from the position in the
   * list — a dismissal has to survive a new reading arriving and reshuffling
   * the order.
   */
  readonly id: string
  readonly kind: NoticingKind
  /** The track it is about. `null` for an observation about the whole database. */
  readonly track: Track | null
  /** Four or five words, for the top line. */
  readonly headline: string
  /** The observation itself, in full, and never a judgement. */
  readonly detail: string
  /** What it was drawn from, so the reader can weigh it. */
  readonly evidence: string
}

export interface NoticingOptions {
  /** Cap on the list. Silently dropping the rest is stated by the caller. */
  readonly limit?: number
  /** Ids the user has hidden. */
  readonly dismissed?: readonly string[]
}

/**
 * Most one track may contribute.
 *
 * Without this, a single track that happens to trip three rules at once fills
 * the whole list and every other track is invisible — which turns a summary of
 * the database into a report on whichever track is currently most eventful.
 */
const PER_TRACK = 2

/** How many readings a rule needs before it will say anything at all. */
export const MINIMUM_READINGS = 8

/** A run of this many at one extreme is worth mentioning. */
const RUN_LENGTH = 3

/** Days without a reading after which returning is worth mentioning. */
const GAP_DAYS = 7

function quantitativePoints(
  track: Track,
  entries: readonly Entry[],
  range: EpochRange,
): readonly QuantitativePoint[] {
  const series = buildSeries(track, entries, range)
  return series.kind === 'quantitative' ? series.points : []
}

function allPoints(
  track: Track,
  entries: readonly Entry[],
  range: EpochRange,
): readonly SeriesPoint[] {
  const series = buildSeries(track, entries, range)
  return series.points
}

function mean(values: readonly number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length
}

/**
 * The last few readings are the lowest, or the highest, of everything here.
 *
 * A fact, and one nobody would spot by eye on a chart of sixty points. Stated as
 * a rank rather than as a trend on purpose: `trendOf` already answers "is it
 * moving", and "the last three are the three lowest" is a different and sharper
 * observation that a gentle slope would not produce.
 */
function runOfExtremes(
  track: Track,
  points: readonly QuantitativePoint[],
): Noticing | null {
  if (points.length < MINIMUM_READINGS) {
    return null
  }
  const recent = points.slice(-RUN_LENGTH)
  const earlier = points.slice(0, -RUN_LENGTH)
  if (recent.length < RUN_LENGTH || earlier.length === 0) {
    return null
  }

  const recentValues = recent.map((point) => point.quantity)
  const earlierValues = earlier.map((point) => point.quantity)
  const lowest = Math.max(...recentValues) < Math.min(...earlierValues)
  const highest = Math.min(...recentValues) > Math.max(...earlierValues)
  if (!lowest && !highest) {
    return null
  }

  const end = lowest ? 'lowest' : 'highest'
  return {
    id: `run:${track.id}:${end}:${points[points.length - 1]?.entryId ?? ''}`,
    kind: 'run',
    track,
    headline: `Last ${String(RUN_LENGTH)} are the ${end}`,
    detail: `The last ${String(RUN_LENGTH)} readings of ${track.name} are the ${end} ${String(RUN_LENGTH)} in this range.`,
    evidence: `${String(points.length)} readings.`,
  }
}

/**
 * The same track is recorded at a noticeably different time at weekends.
 *
 * The analysis ADR-0006 exists for. `minutesSinceMidnight` reads the clock that
 * was on the wall when the entry was made, so this survives a clock change and a
 * holiday — computing it from the bare instant would move every entry by an hour
 * and invent a shift that is not there.
 */
function weekendTiming(track: Track, points: readonly SeriesPoint[]): Noticing | null {
  const weekday: number[] = []
  const weekend: number[] = []
  for (const point of points) {
    // ISO numbering, so 6 and 7 are Saturday and Sunday.
    const day = weekdayOf(wallClockOf(point.at))
    ;(day >= 6 ? weekend : weekday).push(minutesSinceMidnight(point.at))
  }
  // Four of each, so this is a pattern rather than one lie-in.
  if (weekday.length < 4 || weekend.length < 4) {
    return null
  }

  const difference = mean(weekend) - mean(weekday)
  // 45 minutes. Below that it is the noise of when somebody happens to pick up
  // their phone, and calling it a pattern would be inventing one.
  if (Math.abs(difference) < 45) {
    return null
  }

  const hours = Math.abs(difference) / 60
  const rounded = hours >= 1.5 ? `${String(Math.round(hours))} hours` : 'about an hour'
  return {
    id: `timing:${track.id}:${difference > 0 ? 'later' : 'earlier'}`,
    kind: 'timing',
    track,
    headline: `Recorded ${difference > 0 ? 'later' : 'earlier'} at weekends`,
    detail: `${track.name} is usually recorded ${rounded} ${difference > 0 ? 'later' : 'earlier'} on Saturdays and Sundays.`,
    evidence: `${String(weekday.length)} weekday readings and ${String(weekend.length)} at weekends.`,
  }
}

/**
 * One weekday's readings sit apart from the rest of the week.
 *
 * Reported as "higher on Mondays than on other days", never as a cause. The
 * threshold is a share of the track's own spread rather than a fixed number,
 * because a 1-point gap means something different on a 1–10 scale than on a
 * track that runs to hundreds.
 */
function weekdayPattern(
  track: Track,
  points: readonly QuantitativePoint[],
): Noticing | null {
  if (points.length < MINIMUM_READINGS * 2) {
    return null
  }
  const byDay = new Map<Weekday, number[]>()
  for (const point of points) {
    const day = weekdayOf(wallClockOf(point.at))
    byDay.set(day, [...(byDay.get(day) ?? []), point.quantity])
  }

  const all = points.map((point) => point.quantity)
  const spread = Math.max(...all) - Math.min(...all)
  if (spread === 0) {
    return null
  }
  const overall = mean(all)

  let strongest: { day: Weekday; delta: number; count: number } | null = null
  for (const [day, values] of byDay) {
    // Three readings on that weekday at minimum. Two is a coincidence with a
    // name.
    if (values.length < 3) {
      continue
    }
    const delta = mean(values) - overall
    if (strongest === null || Math.abs(delta) > Math.abs(strongest.delta)) {
      strongest = { day, delta, count: values.length }
    }
  }

  // A quarter of the whole spread. Anything less is a difference the reader
  // would not be able to see on the chart it came from.
  if (strongest === null || Math.abs(strongest.delta) < spread * 0.25) {
    return null
  }

  const direction = strongest.delta > 0 ? 'higher' : 'lower'
  const name = weekdayName(strongest.day)
  return {
    id: `weekday:${track.id}:${String(strongest.day)}:${direction}`,
    kind: 'weekday',
    track,
    headline: `${direction === 'higher' ? 'Higher' : 'Lower'} on ${name}s`,
    detail: `${track.name} tends to be ${direction} on ${name}s than on other days.`,
    evidence: `${String(strongest.count)} ${name} readings out of ${String(points.length)}.`,
  }
}

/**
 * Readings on days carrying a tag differ from readings on days without it.
 *
 * The one rule here that connects two things the user recorded separately, which
 * is why it is worth having — and why the wording has to be the most careful.
 * "Lower on days tagged busy" is an observation. "Being busy lowers your sleep"
 * is a claim this app cannot make and must not imply.
 */
function tagContrast(
  track: Track,
  points: readonly QuantitativePoint[],
  entries: readonly Entry[],
  tagNames: ReadonlyMap<string, string>,
): Noticing | null {
  if (points.length < MINIMUM_READINGS) {
    return null
  }
  const tagsByEntry = new Map(entries.map((entry) => [entry.id, entry.tagIds]))
  const all = points.map((point) => point.quantity)
  const spread = Math.max(...all) - Math.min(...all)
  if (spread === 0) {
    return null
  }

  let strongest: { tagId: string; delta: number; withCount: number } | null = null
  for (const tagId of tagNames.keys()) {
    const withTag: number[] = []
    const without: number[] = []
    for (const point of points) {
      const tags = tagsByEntry.get(point.entryId) ?? []
      ;(tags.includes(tagId) ? withTag : without).push(point.quantity)
    }
    if (withTag.length < 4 || without.length < 4) {
      continue
    }
    const delta = mean(withTag) - mean(without)
    if (strongest === null || Math.abs(delta) > Math.abs(strongest.delta)) {
      strongest = { tagId, delta, withCount: withTag.length }
    }
  }

  if (strongest === null || Math.abs(strongest.delta) < spread * 0.3) {
    return null
  }
  const name = tagNames.get(strongest.tagId) ?? ''
  const direction = strongest.delta > 0 ? 'higher' : 'lower'
  return {
    id: `tag:${track.id}:${strongest.tagId}:${direction}`,
    kind: 'tag',
    track,
    headline: `${direction === 'higher' ? 'Higher' : 'Lower'} when tagged ${name}`,
    detail: `${track.name} tends to be ${direction} on the entries you tagged ${name} than on the ones you did not.`,
    evidence: `${String(strongest.withCount)} tagged readings out of ${String(points.length)}.`,
  }
}

/**
 * Recorded again, after a stretch of not being recorded.
 *
 * Deliberately not framed as a broken streak or a lapse. T-903 settled that this
 * app does not apply that kind of pressure, and the honest version of the same
 * fact is the useful one: it says a chart of this track has a hole in it, which
 * is worth knowing before drawing a conclusion from it.
 */
function returnAfterGap(
  track: Track,
  points: readonly SeriesPoint[],
  now: Timestamp,
): Noticing | null {
  if (points.length < 2) {
    return null
  }
  const last = points[points.length - 1]!
  const previous = points[points.length - 2]!
  const days = Math.round(
    (Date.parse(`${dayKey(last.at)}T00:00:00Z`) -
      Date.parse(`${dayKey(previous.at)}T00:00:00Z`)) /
      86_400_000,
  )
  if (days < GAP_DAYS) {
    return null
  }
  // Only while it is still news. A gap that ended a month ago is history, and
  // reporting it as though it just happened is how a list stops being current.
  const sinceLast = Math.round(
    (Date.parse(`${dayKey(now)}T00:00:00Z`) -
      Date.parse(`${dayKey(last.at)}T00:00:00Z`)) /
      86_400_000,
  )
  if (sinceLast > 2) {
    return null
  }
  return {
    id: `return:${track.id}:${last.entryId}`,
    kind: 'return',
    track,
    headline: `Back after ${String(days)} days`,
    detail: `${track.name} was recorded again after ${String(days)} days without it, so charts of this range have a gap in them.`,
    evidence: `${String(points.length)} readings in this range.`,
  }
}

export interface NoticingInput {
  readonly tracks: readonly Track[]
  readonly entries: readonly Entry[]
  readonly tags: readonly { readonly id: string; readonly name: string }[]
  readonly range: EpochRange
  readonly now: Timestamp
}

/**
 * Everything worth saying about this data, which is usually nothing.
 *
 * Order is by rule, not by strength: a noticing is either worth showing or it is
 * not, and sorting by how big the number is turns a neutral list into a ranking
 * of things to worry about.
 */
export function noticings(
  input: NoticingInput,
  options: NoticingOptions = {},
): readonly Noticing[] {
  const dismissed = new Set(options.dismissed ?? [])
  const tagNames = new Map(input.tags.map((tag) => [tag.id, tag.name]))
  const found: Noticing[] = []

  for (const track of input.tracks) {
    if (track.archived) {
      continue
    }
    const points = allPoints(track, input.entries, input.range)
    if (points.length === 0) {
      continue
    }
    const quantities = isQuantitative(track.kind)
      ? quantitativePoints(track, input.entries, input.range)
      : []

    let fromThisTrack = 0
    for (const candidate of [
      // In this order, so what survives the per-track cap is the sharpest
      // observation rather than whichever rule happens to be listed first.
      returnAfterGap(track, points, input.now),
      runOfExtremes(track, quantities),
      tagContrast(track, quantities, input.entries, tagNames),
      weekdayPattern(track, quantities),
      weekendTiming(track, points),
    ]) {
      if (fromThisTrack >= PER_TRACK) {
        break
      }
      if (candidate !== null && !dismissed.has(candidate.id)) {
        found.push(candidate)
        fromThisTrack += 1
      }
    }
  }

  return options.limit === undefined ? found : found.slice(0, options.limit)
}
